<template>
  <ButtonSquare
    :title="$t('general__view__sensordata')"
    :imgSource="iconSensor"
    size="large"
    :activeState="activeState"
    :showLoadingSpinner="isLoadingSensordata"
    :hoverButtons="hoverButtons"
    :activeHoverButtonValue="currentSensordataType"
    @clicked="toggle"
    @onHoverButtonClick="changeSensordataType"
    @mouseenter="onHover"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useFeature } from '@/pinia/features'
import ButtonSquare, { Button } from '@/components/general/buttons/ButtonSquare/ButtonSquare.vue'
import iconSensor from '@/assets/sensor-icon.svg'
import { MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { SET_IS_SENSORDATA_SHOWING, SET_SENSORDATA_TYPE } from '@/store/mutation-types'

// Sensor type to role mapping
const sensorDataTypeToRole = {
  temperature: 'FEAT_SENSOR-TEMPERATURE_READER',
  co2: 'FEAT_SENSOR-CO2_READER',
  humidity: 'FEAT_SENSOR-HUMIDITY_READER',
  presence: 'FEAT_SENSOR-PRESENCE_READER',
  flow: 'FEAT_SENSOR-FLOW_READER'
}

// Emits
const emit = defineEmits(['SensorDataButtonHovered'])

// Store and feature
const store = useStore()
const { feature } = useFeature('home')

// State
const hoverButtons = ref<Button[]>([])
const updateSensor = ref<any>(null)

// Vuex state
const currentSensordataType = computed(() => store.state[PLANVIEW_PREFIX].currentSensordataType)
const isSensordataShowing = computed(() => store.state[PLANVIEW_PREFIX].isSensordataShowing)
const isLoadingSensordata = computed(() => store.state[PLANVIEW_PREFIX].isLoadingSensordata)
const sensordataTypesForSelectedLevel = computed(
  () => store.state[PLANVIEW_PREFIX].sensordataTypesForSelectedLevel
)
const selectedBuilding = computed(() => store.state[MAPVIEW_PREFIX].selectedBuilding)
const featureAccess = computed(() => store.state.featureAccess)

// Compute accepted sensor data types based on feature access
const acceptedSensordataTypes = computed(() => {
  const acceptedTypes = feature.value?.props?.acceptedSensordataTypes ?? [
    'co2',
    'flow',
    'humidity',
    'presence',
    'temperature'
  ]
  const filteredTypes = acceptedTypes.filter((type: string) =>
    featureAccess.value.includes(sensorDataTypeToRole[type])
  )
  return filteredTypes
})

// Compute active state for the button
const activeState = computed(() => {
  if (hoverButtons.value.length) return isSensordataShowing.value ? 'active' : 'inactive'
  return 'deactivated'
})

// Watch for changes in available sensor types for the selected level
watch(
  sensordataTypesForSelectedLevel,
  () => {
    hoverButtons.value = []
    if (!sensordataTypesForSelectedLevel.value.length) {
      hoverButtons.value = getExtraHoverButtons()
    } else {
      const buttons = getHoverButtons(sensordataTypesForSelectedLevel.value)
      const extraButtons = getExtraHoverButtons()
      hoverButtons.value = [...buttons, ...extraButtons]
    }
    if (currentSensordataType.value && isSensordataShowing.value) return
    const firstEnabledButton = hoverButtons.value.find((button) => !button.disabled)
    setSensordataType({ type: firstEnabledButton ? firstEnabledButton.emit : null })
  },
  { immediate: true }
)

// Cleanup on unmount
onUnmounted(() => {
  setSensordataType({ type: null })
  clearInterval(updateSensor.value)
  updateSensor.value = null
})

// Mutations and actions
function setInfoMessage(payload: any) {
  store.commit('setInfoMessage', payload)
}
function setIsSensordataShowing(payload: any) {
  store.commit(`${PLANVIEW_PREFIX}/${SET_IS_SENSORDATA_SHOWING}`, payload)
}
function setSensordataType(payload: any) {
  store.commit(`${PLANVIEW_PREFIX}/${SET_SENSORDATA_TYPE}`, payload)
}
function loadSensorData() {
  return store.dispatch(`${PLANVIEW_PREFIX}/loadSensorData`)
}

// Hover event
function onHover() {
  if (activeState.value !== 'deactivated') {
    emit('SensorDataButtonHovered')
  }
}

// Change sensor data type
function changeSensordataType(type: string) {
  setIsSensordataShowing({ value: true })
  setSensordataType({ type })
  if (updateSensor.value == null) updateSensordata()
}

// Periodically update sensor data
function updateSensordata() {
  loadSensorData()
  updateSensor.value = setInterval(() => {
    loadSensorData().then(() =>
      setInfoMessage({
        type: 'success',
        message: store.getters['$t']?.('message__sensor-data-fetch-update') ?? ''
      })
    )
  }, 120000)
}

// Toggle button state
function toggle() {
  if (activeState.value === 'deactivated') return null
  if (activeState.value === 'inactive') updateSensordata()
  if (activeState.value === 'active') {
    clearInterval(updateSensor.value)
    updateSensor.value = null
  }
  setIsSensordataShowing({ value: !isSensordataShowing.value })
  if (!isCurrentTypeInHoverButtons()) {
    const firstEnabledButton = hoverButtons.value.find((button) => !button.disabled)
    setSensordataType({ type: firstEnabledButton ? firstEnabledButton.emit : null })
  }
}

// Build hover buttons for available sensor types
function getHoverButtons(sensordataTypes: string[]): Button[] {
  return sensordataTypes
    .filter((type) => acceptedSensordataTypes.value.includes(type.toLowerCase()))
    .map((type) => {
      const lowerCaseType = type.toLowerCase()
      return {
        title: store.getters['$t']?.(`general__data__sensor-${lowerCaseType}`) ?? lowerCaseType,
        emit: lowerCaseType
      }
    })
}

// Add extra hover buttons for specific buildings
function getExtraHoverButtons(): Button[] {
  const buttons: Button[] = []
  // Adding hover alternatives specific for VE
  if (selectedBuilding.value.id === '774289046017777664') {
    buttons.push({
      title: store.getters['$t']?.('general__data__sensor-temperature') ?? 'Temperature',
      emit: 'temperature'
    })
  }
  // Adding hover alternatives specific for ICA Enköping
  if (selectedBuilding.value.id === '709333890242998272') {
    buttons.push({ title: 'Wifi-täckning [demo]', emit: 'wifi' })
  }
  // Adding hover alternatives specific for AWL
  if (selectedBuilding.value.id === '834089140215631872') {
    buttons.push(
      { title: 'Bokningsbara rum', emit: 'booking', disabled: true },
      { title: 'Wifi-täckning [demo]', emit: 'wifi' }
      // { title: 'Luftflöde', emit: 'flow' }
    )
  }
  return buttons
}

// Check if current type is in hover buttons
function isCurrentTypeInHoverButtons() {
  return hoverButtons.value.find((button) => button.emit === currentSensordataType.value)
}
</script>
